import { logger } from '@/lib/logger';
import React from 'react';
import ErrorBoundary from '@/components/ErrorBoundary';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from '@/components/shared/Layout';
import PrivateRoute from './components/PrivateRoute';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { CompanyProvider } from '@/contexts/CompanyContext';
import { QualityProvider } from '@/contexts/QualityContext';
import { TenantProvider } from '@/contexts/TenantContext';
import SettingsLayout from '@/modules/Administration/components/settings/settings';
import AdminPortal from './modules/Administration/pages/AdminPortal';
import Toaster from '@/core/oblique-ui/Toaster';

// Operations Pages (Lazy-loaded for smooth routing)
const HomePage = React.lazy(() => import('./modules/CRM/pages/HomePage'));
const CRMLeadsPage = React.lazy(() => import('./modules/CRM/pages/CRMLeadsPage'));
const DealPipelinePage = React.lazy(() => import('./modules/CRM/pages/DealPipelinePage'));
const LeadsPage = React.lazy(() => import('./modules/CRM/pages/Operations/LeadsPage'));
const CompaniesPage = React.lazy(() => import('./modules/CRM/pages/Operations/CompaniesPage'));
const DealsPage = React.lazy(() => import('./modules/CRM/pages/Operations/DealsPage'));
const ActivitiesPage = React.lazy(() => import('./modules/CRM/pages/Operations/ActivitiesPage'));
const CRMReportsPage = React.lazy(() => import('./modules/CRM/pages/CRMReportsPage'));
const CRMCommunicationPage = React.lazy(() => import('./modules/CRM/pages/CRMCommunicationPage'));
const EmailsPage = React.lazy(() => import('./modules/CRM/pages/Operations/EmailsPage'));
const SequencesPage = React.lazy(() => import('./modules/CRM/pages/Operations/SequencesPage'));
const OrdersPage = React.lazy(() => import('./modules/CRM/pages/Operations/OrdersPage'));

// Quality Pages (Lazy-loaded for zero-jank QMS performance)
const QMSDashboard = React.lazy(() => import('./modules/QMS/pages/QualityDashboard'));
const ManagementReview = React.lazy(() => import('./modules/QMS/pages/ManagementReview'));
const EscalationCases = React.lazy(() => import('./modules/QMS/pages/EscalationCases'));
const QualityDashboard = React.lazy(() => import('./modules/QMS/pages/QualityDashboard'));
const DealAudits = React.lazy(() => import('./modules/QMS/pages/DealAudits'));
const DealAuditWorkspace = React.lazy(() => import('./modules/QMS/pages/DealAuditWorkspace'));
const QualityOrdersPage = React.lazy(() => import('./modules/QMS/pages/QualityOrdersPage'));
const CampaignAudits = React.lazy(() => import('./modules/QMS/pages/CampaignAudits'));
const AccountAuditPage = React.lazy(() => import('./modules/QMS/pages/AccountAuditPage'));
const SalesAuditsPage = React.lazy(() => import('./modules/QMS/pages/SalesAuditsPage'));
const QualityPlanner = React.lazy(() => import('./modules/QMS/pages/QualityPlanner'));
const QualityLibrary = React.lazy(() => import('./modules/QMS/pages/QualityLibrary'));
const QADashboard = React.lazy(() => import('./modules/QMS/pages/QADashboard'));

const ControlCenter = React.lazy(() => import('./modules/QMS/pages/ControlCenter'));
const FeedbackPage = React.lazy(() => import('./modules/QMS/pages/FeedbackPage'));
const AgentDashboard = React.lazy(() => import('./modules/QMS/pages/AgentDashboard'));
const AutomationBuilder = React.lazy(() => import('./modules/QMS/pages/AutomationBuilder'));
const TrackerDashboard = React.lazy(() => import('./modules/QMS/pages/TrackerDashboard'));
const LeadAudits = React.lazy(() => import('./modules/QMS/pages/LeadAudits'));
const QualitySettingsPage = React.lazy(() => import('./modules/QMS/pages/QualitySettingsPage'));
const PeopleAudits = React.lazy(() => import('./modules/QMS/pages/PeopleAudits'));
const GoogleDriveTracker = React.lazy(() => import('./modules/QMS/pages/GoogleDriveTracker'));
const EmailAudits = React.lazy(() => import('./modules/QMS/pages/EmailAudits'));
const BAUAuditsPage = React.lazy(() => import('./modules/QMS/pages/BAUAuditsPage'));
const RCATracker = React.lazy(() => import('./modules/QMS/pages/RCATracker'));
const CoachingCenter = React.lazy(() => import('./modules/QMS/pages/CoachingCenter'));
const ComingSoonPage = React.lazy(() => import('./modules/QMS/pages/ComingSoonPage'));
const CalibrationCenter = React.lazy(() => import('./modules/QMS/pages/CalibrationCenter'));

// Analytics Engine (Lazy-loaded)
const AnalyticsDashboard = React.lazy(() => import('./modules/Analytics/components/Dashboard'));
const AutomationsDashboard = React.lazy(() => import('./modules/Orbit/AutomationsDashboard'));

// Hub Pages
const WorkspaceHub = React.lazy(() => import('./pages/WorkspaceHub'));
const AgentPortal = React.lazy(() => import('./modules/Administration/pages/AgentPortal'));

// LMS Pages
const LMSDashboard = React.lazy(() => import('./modules/LMS/pages/LMSDashboard'));
const CourseContent = React.lazy(() => import('./modules/LMS/pages/CourseContent'));
const IntelligencePage = React.lazy(() => import('./modules/LMS/pages/IntelligencePage'));
const SalesPlaybook = React.lazy(() => import('./modules/LMS/pages/SalesPlaybook'));

// Tracker routing now internal to Quality

// Public Pages
import LandingPage from './modules/CRM/pages/LandingPage';
import OrganizationOnboarding from './modules/Administration/pages/OrganizationOnboarding';
import Login from './modules/CRM/pages/Login';
import EscalationForm from './modules/CRM/pages/Public/EscalationForm';
import AboutUs from './modules/CRM/pages/Public/AboutUs';
import PricingPage from './modules/CRM/pages/Public/PricingPage';
import ContactPage from './modules/CRM/pages/Public/ContactPage';
import SecurityPage from './modules/CRM/pages/Public/SecurityPage';
import SoftwarePage from './modules/CRM/pages/Public/SoftwarePage';
import SolutionsPage from './modules/CRM/pages/Public/SolutionsPage';
import ResourcesPage from './modules/CRM/pages/Public/ResourcesPage';
const DeveloperDashboard = React.lazy(() => import('./modules/Developer/pages/DeveloperDashboard'));
const HRDashboard = React.lazy(() => import('./modules/HR/pages/HRDashboard'));
const FinanceDashboard = React.lazy(() => import('./modules/Finance/pages/FinanceDashboard'));
const DesignSystem = React.lazy(() => import('./pages/DesignSystem'));
// import SettingsPage from './pages/SettingsPage';
import './index.css';

const AIModule = React.lazy(() => import('./modules/AI/AIModule'));

// Settings modal handles settings globally

// Basic Error Boundary
class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(_error) {
    if (_error?.message?.includes('INTERNAL ASSERTION FAILED') || String(_error)?.includes('INTERNAL ASSERTION FAILED')) {
      return { hasError: false };
    }
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    if (error?.message?.includes('INTERNAL ASSERTION FAILED') || String(error)?.includes('INTERNAL ASSERTION FAILED')) {
      return;
    }
    this.setState({ error, errorInfo });
    logger.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', fontFamily: 'system-ui', color: '#333' }}>
          <h1>Something went wrong.</h1>
          <details open style={{ whiteSpace: 'pre-wrap', background: '#f5f5f5', padding: '20px', borderRadius: '8px' }}>
            {this.state.error && this.state.error.toString()}
            <br />
            {this.state.errorInfo && this.state.errorInfo.componentStack}
          </details>
          <button onClick={() => window.location.href = '/'} style={{ marginTop: '20px', padding: '10px 20px', background: '#333', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
            Go Home
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Suspense Loading (Standard Spinner)
function Loading() { 
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', width: '100vw', background: 'var(--bg-page)' }}>
       <div style={{ width: '40px', height: '40px', borderRadius: '50%', border: '3px solid var(--border)', borderTopColor: 'var(--accent)', animation: 'spin 1s linear infinite' }} />
    </div>
  );
}

const AdminGuard = ({ children }) => {
  const { user, userRole, loading } = useAuth();
  
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!['superadmin', 'admin'].includes(userRole)) return <Navigate to="/hub" replace />;
  
  return children;
};

// OnboardingGuard removed as per user request to handle onboarding inside the Administration panel.

const NotFoundRedirect = () => {
  const { user } = useAuth();
  return <Navigate to={user ? "/hub" : "/"} replace />;
};

export function App() {
  return (
    <GlobalErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <NotificationProvider>
              <TenantProvider>
                <CompanyProvider>
                  <QualityProvider>
                    <Router>
                      <React.Suspense fallback={<Loading />}>
                        <Routes>
                          {/* Public Routes */}
                          <Route path="/" element={<LandingPage />} />
                          <Route path="/login" element={<Login />} />
                          <Route path="/about" element={<AboutUs />} />
                          <Route path="/pricing" element={<PricingPage />} />
                          <Route path="/contact" element={<ContactPage />} />
                          <Route path="/security" element={<SecurityPage />} />
                          <Route path="/software" element={<SoftwarePage />} />
                          <Route path="/solutions" element={<SolutionsPage />} />
                          <Route path="/resources" element={<ResourcesPage />} />
                          <Route path="/public/escalation-form" element={<EscalationForm />} />
                          <Route path="/design-system" element={
                            <React.Suspense fallback={<Loading />}>
                              <DesignSystem />
                            </React.Suspense>
                          } />

                          {/* Hub Page */}
                          <Route path="/onboarding" element={<PrivateRoute><OrganizationOnboarding /></PrivateRoute>} />
                          <Route path="/hub" element={<PrivateRoute><WorkspaceHub /></PrivateRoute>} />
                          
                          {/* Agent Portal */}
                          <Route path="/agent-portal" element={<AgentPortal />} />

                          {/* Dashboard/App Routes */}
                          <Route element={<PrivateRoute><Layout /></PrivateRoute>}>
                            {/* CRM */}
                            <Route path="crm" element={<ErrorBoundary><HomePage /></ErrorBoundary>} />
                            <Route path="crm/leads" element={<ErrorBoundary><CRMLeadsPage /></ErrorBoundary>} />
                            <Route path="crm/pipeline" element={<ErrorBoundary><DealPipelinePage /></ErrorBoundary>} />
                            <Route path="crm/reports" element={<ErrorBoundary><CRMReportsPage /></ErrorBoundary>} />

                            <Route path="crm/communication" element={<ErrorBoundary><CRMCommunicationPage /></ErrorBoundary>} />
                            <Route path="crm/orders" element={<ErrorBoundary><OrdersPage /></ErrorBoundary>} />
                            <Route path="crm/tickets" element={<ErrorBoundary><EscalationCases /></ErrorBoundary>} />
                            <Route path="crm/inbox" element={<ErrorBoundary><EmailAudits /></ErrorBoundary>} />
                            <Route path="crm/planner" element={<ErrorBoundary><QualityPlanner /></ErrorBoundary>} />
                            
                            {/* Operations */}
                            <Route path="operations/leads" element={<ErrorBoundary><LeadsPage /></ErrorBoundary>} />
                            <Route path="operations/companies" element={<ErrorBoundary><CompaniesPage /></ErrorBoundary>} />
                            <Route path="operations/deals" element={<ErrorBoundary><DealsPage /></ErrorBoundary>} />
                            <Route path="operations/activities" element={<ErrorBoundary><ActivitiesPage /></ErrorBoundary>} />
                            <Route path="operations/emails" element={<ErrorBoundary><EmailsPage /></ErrorBoundary>} />
                            <Route path="operations/sequences" element={<ErrorBoundary><SequencesPage /></ErrorBoundary>} />
                            <Route path="operations/orders" element={<ErrorBoundary><OrdersPage /></ErrorBoundary>} />

                            {/* Quality */}
                            <Route path="quality" element={<ErrorBoundary><QMSDashboard /></ErrorBoundary>} />
                            <Route path="quality/management-review" element={<ErrorBoundary><Navigate to="/quality?tab=management_review" replace /></ErrorBoundary>} />
                            <Route path="quality/escalations" element={<ErrorBoundary><EscalationCases /></ErrorBoundary>} />
                            <Route path="quality/leads" element={<ErrorBoundary><LeadAudits /></ErrorBoundary>} />
                            <Route path="quality/audits" element={<ErrorBoundary><BAUAuditsPage /></ErrorBoundary>} />
                            {/* Redirect old individual audit routes to unified page */}
                            <Route path="quality/calls" element={<ErrorBoundary><Navigate to="/quality/audits?type=calls" replace /></ErrorBoundary>} />
                            <Route path="quality/emails" element={<ErrorBoundary><Navigate to="/quality/audits?type=emails" replace /></ErrorBoundary>} />
                            <Route path="quality/chats" element={<ErrorBoundary><Navigate to="/quality/audits?type=chats" replace /></ErrorBoundary>} />
                            <Route path="quality/meetings" element={<ErrorBoundary><Navigate to="/quality/audits?type=videos" replace /></ErrorBoundary>} />
                            <Route path="quality/analytics" element={<ErrorBoundary><QADashboard /></ErrorBoundary>} />
                            
                            {/* QualityOS Audits */}

                            <Route path="quality/deals/audit/:id" element={<ErrorBoundary><DealAuditWorkspace /></ErrorBoundary>} />
                            <Route path="quality/orders" element={<ErrorBoundary><QualityOrdersPage /></ErrorBoundary>} />
                            {/* QualityOS Audits (Dummy routes removed) */}

                            {/* QualityOS Governance */}
                            <Route path="quality/calibration" element={<ErrorBoundary><CalibrationCenter /></ErrorBoundary>} />
                            <Route path="quality/coaching" element={<ErrorBoundary><CoachingCenter /></ErrorBoundary>} />
                            <Route path="quality/agent-dashboard" element={<ErrorBoundary><AgentDashboard /></ErrorBoundary>} />

                            {/* QualityOS Continuous Improvement */}
                            <Route path="quality/rca" element={<ErrorBoundary><RCATracker /></ErrorBoundary>} />
                            {/* QualityOS Continuous Improvement (Dummy routes removed) */}

                            {/* QualityOS Compliance & Risk */}
                            <Route path="quality/sops" element={<ErrorBoundary><QualityLibrary /></ErrorBoundary>} />
                            {/* QualityOS Compliance & Risk (Dummy routes removed) */}


                            {/* Operations & Legacy Quality Routes */}
                            <Route path="quality/campaigns" element={<ErrorBoundary><CampaignAudits /></ErrorBoundary>} />
                            <Route path="quality/accounts" element={<ErrorBoundary><AccountAuditPage /></ErrorBoundary>} />
                            <Route path="quality/sales-audits" element={<ErrorBoundary><DealAudits viewMode="deals" /></ErrorBoundary>} />
                            <Route path="quality/ai-audits" element={<ErrorBoundary><SalesAuditsPage /></ErrorBoundary>} />
                            <Route path="quality/deals/audit/:id" element={<ErrorBoundary><DealAuditWorkspace /></ErrorBoundary>} />
                            <Route path="quality/planner" element={<ErrorBoundary><QualityPlanner /></ErrorBoundary>} />
                            <Route path="quality/library" element={<ErrorBoundary><QualityLibrary /></ErrorBoundary>} />
                            <Route path="quality/yoda" element={<ErrorBoundary><QualityLibrary /></ErrorBoundary>} />
                            {/* YODA Playlists removed */}
                            <Route path="quality/control-center" element={<ErrorBoundary><ControlCenter /></ErrorBoundary>} />
                            {/* Workflows removed */}
                            <Route path="quality/feedback" element={<ErrorBoundary><FeedbackPage /></ErrorBoundary>} />
                            <Route path="quality/tracker/sops" element={<ErrorBoundary><Navigate to="/quality/leads" replace /></ErrorBoundary>} />
                            <Route path="quality/people" element={<ErrorBoundary><PeopleAudits /></ErrorBoundary>} />
                            <Route path="quality/compliance" element={<ErrorBoundary><GoogleDriveTracker /></ErrorBoundary>} />
                            <Route path="quality/settings" element={<ErrorBoundary><QualitySettingsPage /></ErrorBoundary>} />

                            {/* QMS Aliases for /qms paths */}
                            <Route path="qms" element={<ErrorBoundary><Navigate to="/quality" replace /></ErrorBoundary>} />
                            <Route path="qms/sales-audits" element={<ErrorBoundary><Navigate to="/quality/sales-audits" replace /></ErrorBoundary>} />
                            <Route path="qms/escalations" element={<ErrorBoundary><Navigate to="/quality/escalations" replace /></ErrorBoundary>} />
                            <Route path="qms/leads" element={<ErrorBoundary><Navigate to="/quality/leads" replace /></ErrorBoundary>} />
                            <Route path="qms/audits" element={<ErrorBoundary><Navigate to="/quality/audits" replace /></ErrorBoundary>} />
                            <Route path="qms/sops" element={<ErrorBoundary><Navigate to="/quality/sops" replace /></ErrorBoundary>} />
                            <Route path="qms/campaigns" element={<ErrorBoundary><Navigate to="/quality/campaigns" replace /></ErrorBoundary>} />
                            <Route path="qms/accounts" element={<ErrorBoundary><Navigate to="/quality/accounts" replace /></ErrorBoundary>} />
                            <Route path="qms/ai-audits" element={<ErrorBoundary><Navigate to="/quality/ai-audits" replace /></ErrorBoundary>} />
                            <Route path="qms/calibration" element={<ErrorBoundary><Navigate to="/quality/calibration" replace /></ErrorBoundary>} />
                            <Route path="qms/coaching" element={<ErrorBoundary><Navigate to="/quality/coaching" replace /></ErrorBoundary>} />
                            <Route path="qms/rca" element={<ErrorBoundary><Navigate to="/quality/rca" replace /></ErrorBoundary>} />
                            <Route path="qms/settings" element={<ErrorBoundary><Navigate to="/quality/settings" replace /></ErrorBoundary>} />
                            <Route path="qms/*" element={<ErrorBoundary><Navigate to="/quality" replace /></ErrorBoundary>} />

                            {/* Analytics */}
                            <Route path="analytics" element={<ErrorBoundary><AnalyticsDashboard /></ErrorBoundary>} />
                            <Route path="analytics/:activeTab" element={<ErrorBoundary><AnalyticsDashboard /></ErrorBoundary>} />

                            {/* Manager / Admin / Settings */}
                            <Route path="settings/*" element={<ErrorBoundary><SettingsLayout /></ErrorBoundary>} />
                            <Route path="manager" element={<ErrorBoundary><Navigate to="/hub" replace /></ErrorBoundary>} />
                            <Route path="admin/*" element={<ErrorBoundary><AdminPortal /></ErrorBoundary>} />
                            {/* LMS */}
                            <Route path="lms" element={<ErrorBoundary><LMSDashboard /></ErrorBoundary>} />
                            <Route path="lms/courses" element={<ErrorBoundary><LMSDashboard /></ErrorBoundary>} />
                            <Route path="lms/playbook" element={<ErrorBoundary><SalesPlaybook /></ErrorBoundary>} />
                            <Route path="lms/planner" element={<ErrorBoundary><QualityPlanner /></ErrorBoundary>} />
                            <Route path="lms/intelligence" element={<ErrorBoundary><SalesPlaybook /></ErrorBoundary>} />
                            <Route path="lms/:courseId" element={<ErrorBoundary><CourseContent /></ErrorBoundary>} />
                            <Route path="automations" element={<PrivateRoute><AutomationsDashboard /></PrivateRoute>} />
                            <Route path="billing" element={<ErrorBoundary><Navigate to="/hub" replace /></ErrorBoundary>} />
                            
                            {/* Tracker moved to Quality */}

                            {/* HR */}
                            <Route path="hr" element={<ErrorBoundary><HRDashboard /></ErrorBoundary>} />
                            <Route path="hr/*" element={<ErrorBoundary><HRDashboard /></ErrorBoundary>} />

                            {/* Finance */}
                            <Route path="finance" element={<ErrorBoundary><FinanceDashboard /></ErrorBoundary>} />
                            <Route path="finance/*" element={<ErrorBoundary><FinanceDashboard /></ErrorBoundary>} />

                            {/* Developer */}
                            <Route path="developer" element={<ErrorBoundary><DeveloperDashboard /></ErrorBoundary>} />
                            <Route path="developer/*" element={<ErrorBoundary><DeveloperDashboard /></ErrorBoundary>} />
                            
                            {/* AI */}
                            <Route path="ai/*" element={<ErrorBoundary><AIModule /></ErrorBoundary>} />
                          </Route>

                          {/* Fallback */}
                          <Route path="*" element={<ErrorBoundary><NotFoundRedirect /></ErrorBoundary>} />
                        </Routes>
                      </React.Suspense>
                      <Toaster position="bottom-right" />
                    </Router>
                  </QualityProvider>
                </CompanyProvider>
              </TenantProvider>
          </NotificationProvider>
        </AuthProvider>
      </ThemeProvider>
    </GlobalErrorBoundary>
  );
}

export default App;
